import { BUILD_INFO } from "../generatedBuildInfo";

const APP_CACHE_PREFIX = "manucalc";
const UPDATE_RELOAD_KEY = "manucalc.lastAcceptedUpdateReload";
const UPDATE_LATER_KEY = "manucalc.updateLaterBuild";
const BUILD_INFO_URL = "/build-info.json";

export const shouldRefreshForBuild = (remoteBuildInfo = {}, currentBuildInfo = BUILD_INFO) =>
  Boolean(
    remoteBuildInfo?.buildTimestamp &&
      currentBuildInfo?.buildTimestamp &&
      remoteBuildInfo.buildTimestamp !== currentBuildInfo.buildTimestamp
  );

export const getBuildSignature = (buildInfo = {}) =>
  [buildInfo.version, buildInfo.buildTimestamp, buildInfo.commit]
    .filter(Boolean)
    .map(String)
    .join("|");

export const clearManuCalcCaches = async () => {
  if (typeof caches === "undefined") return [];
  const keys = await caches.keys();
  const manucalcKeys = keys.filter((key) => key.startsWith(APP_CACHE_PREFIX));
  await Promise.all(manucalcKeys.map((key) => caches.delete(key)));
  return manucalcKeys;
};

export const requestServiceWorkerUpdate = async () => {
  if (typeof navigator === "undefined" || !navigator.serviceWorker?.getRegistrations) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.map(async (registration) => {
      await registration.update?.();
    })
  );
};

export const fetchLiveBuildInfo = async () => {
  const response = await fetch(`${BUILD_INFO_URL}?update=${Date.now()}`, {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });
  if (!response.ok) throw new Error("Live build info could not be checked.");
  return response.json();
};

export const getServiceWorkerRegistration = async () => {
  if (typeof navigator === "undefined" || !navigator.serviceWorker?.ready) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
};

export const activateWaitingServiceWorker = async (registration) => {
  if (!registration?.waiting) return false;
  registration.waiting.postMessage({ type: "SKIP_WAITING" });
  return true;
};

export const clearInstalledAppCache = async () => {
  await clearManuCalcCaches();

  if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    registrations.forEach((registration) => {
      registration.active?.postMessage({ type: "CLEAR_MANUCALC_CACHES" });
      registration.waiting?.postMessage({ type: "CLEAR_MANUCALC_CACHES" });
    });
  }

  return true;
};

export const checkForAvailableAppUpdate = async ({
  currentBuildInfo = BUILD_INFO,
  storage = typeof window !== "undefined" ? window.sessionStorage : null,
} = {}) => {
  if (typeof window === "undefined" || typeof fetch === "undefined") return false;

  const remoteBuildInfo = await fetchLiveBuildInfo();
  if (!shouldRefreshForBuild(remoteBuildInfo, currentBuildInfo)) {
    storage?.removeItem(UPDATE_LATER_KEY);
    return null;
  }

  await requestServiceWorkerUpdate();
  const registration = await getServiceWorkerRegistration();
  const remoteSignature = getBuildSignature(remoteBuildInfo);

  if (storage?.getItem(UPDATE_LATER_KEY) === remoteSignature) {
    return null;
  }

  return {
    currentBuildInfo,
    remoteBuildInfo,
    registration,
    hasWaitingWorker: Boolean(registration?.waiting),
    remoteSignature,
  };
};

export const deferAppUpdate = (remoteBuildInfo, storage = typeof window !== "undefined" ? window.sessionStorage : null) => {
  const signature = getBuildSignature(remoteBuildInfo);
  if (signature) storage?.setItem(UPDATE_LATER_KEY, signature);
};

export const applyAvailableAppUpdate = async ({
  registration,
  remoteBuildInfo,
  reload = () => window.location.reload(),
  storage = typeof window !== "undefined" ? window.sessionStorage : null,
} = {}) => {
  const remoteSignature = getBuildSignature(remoteBuildInfo);
  if (remoteSignature && storage?.getItem(UPDATE_RELOAD_KEY) === remoteSignature) return false;
  if (remoteSignature) storage?.setItem(UPDATE_RELOAD_KEY, remoteSignature);
  storage?.removeItem(UPDATE_LATER_KEY);

  const activeRegistration = registration || (await getServiceWorkerRegistration());
  const activated = await activateWaitingServiceWorker(activeRegistration);
  if (!activated) {
    await clearInstalledAppCache();
    reload();
  }
  return true;
};

export const forceCacheRecoveryReload = async ({
  reload = () => window.location.reload(),
  storage = typeof window !== "undefined" ? window.sessionStorage : null,
} = {}) => {
  await clearInstalledAppCache();
  storage?.removeItem(UPDATE_LATER_KEY);
  storage?.removeItem(UPDATE_RELOAD_KEY);
  reload();
  return true;
};

export const ensureLatestAppVersion = checkForAvailableAppUpdate;
